import React, { useState, useEffect } from 'react';
import { DefaultEditor } from 'react-simple-wysiwyg';
import { Modal, ModalHeader, ModalBody, ModalFooter } from './modal';
import { Button } from './button';
import { Label } from './label';
import FileUpload from './FileUpload';
import { lessonContentAPI, cacheUtils, quizAPI, questionAPI } from '../../services/api';

const LessonContentModal = ({ 
  isOpen, 
  onClose, 
  lessonId,
  lessonTitle, 
  editingContent = null,
  onSuccess,
  isViewingMode = false,
  existingContents = [],
  onContentDeleted = null
}) => {
  const [contentForms, setContentForms] = useState([
    {
      id: 1,
      lesson_id: lessonId || '',
      title: '',
      content_type: 0,
      content: '',
      content_text: '',
      quiz_id: null,
      question_id: null,
      order_index : 1
    }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState({});
  const [quizzes, setQuizzes] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [quizzesLoading, setQuizzesLoading] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [nextId, setNextId] = useState(2);
  const [deletingContentId, setDeletingContentId] = useState(null);

  const contentTypes = [
    { value: 1, label: 'Text Content'},
    { value: 2, label: 'Video' },
    { value: 3, label: 'Quiz' },
    { value: 4, label: 'Image' },
    { value: 5, label: 'Question'}
  ];

  useEffect(() => {
    if (isOpen) {
      if (isViewingMode && existingContents.length > 0) {
        // Viewing existing contents - populate forms with existing data
        const formattedContents = existingContents.map((content, index) => ({
          id: index + 1,
          lesson_id: content.lesson_id || lessonId,
          title: content.title || '',
          content_type: content.content_type || 1,
          content: content.content || '',
          content_text: content.content_text || '',
          quiz_id: content.quiz_id || null,
          question_id: content.question_id || null,
          order_index: content.order_index !== undefined ? content.order_index : index,
          existing_id: content.id // Store the original ID for updates
        }));
        setContentForms(formattedContents);
        setNextId(formattedContents.length + 1);
      } else if (editingContent) {
        // Editing existing content - single form
        setContentForms([{
          id: 1,
          lesson_id: editingContent.lesson_id || lessonId,
          title: editingContent.title || '',
          content_type: editingContent.content_type || 1,
          content: editingContent.content || '',
          content_text: editingContent.content_text || '',
          quiz_id: editingContent.quiz_id || null,
          question_id: editingContent.question_id || null,
          order_index: editingContent.order_index !== undefined ? editingContent.order_index : 1
        }]);
        setNextId(2);
      } else {
        // Creating new content - start with one form
        setContentForms([{
          id: 1,
          lesson_id: lessonId || 0,
          title: '',
          content_type: 0,
          content: '',
          content_text: '',
          quiz_id: null,
          question_id: null,
          order_index: 1
        }]);
        setNextId(2);
      }
      setUploadError({});
      
      // Fetch quizzes and questions when modal opens
      fetchQuizzes();
      fetchQuestions();
    }
  }, [isOpen, editingContent, lessonId, isViewingMode, existingContents]);

  const fetchQuizzes = async () => {
    try {
      setQuizzesLoading(true);
      const response = await quizAPI.getAllQuizzes();
      setQuizzes(response.data || []);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
      setQuizzes([]);
    } finally {
      setQuizzesLoading(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      setQuestionsLoading(true);
      // Fetch questions filtered by lessonId if available
      if (lessonId) {
        const response = await questionAPI.getQuestionsByLessonId(lessonId);
        setQuestions(response.data || []);
      } else {
        setQuestions([]);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      setQuestions([]);
    } finally {
      setQuestionsLoading(false);
    }
  };

  const handleInputChange = (formId, e) => {
    const { name, value, type } = e.target;
    setContentForms(prev => prev.map(form => 
      form.id === formId 
        ? {
            ...form,
            [name]: type === 'number' 
              ? parseInt(value) || 0 
              : value === 'null' || value === ''
                ? null
                : value
          }
        : form
    ));

    // Clear content when content_type changes
    if (name === 'content_type') {
      setContentForms(prev => prev.map(form => 
        form.id === formId 
          ? {
              ...form,
              content: '',
              quiz_id: null,
              question_id: null
            }
          : form
      ));
    }
  };

  // Handle direct content field changes (for file selection)
  const handleContentChange = (formId, fieldName, value) => {
    setContentForms(prev => prev.map(form => 
      form.id === formId 
        ? { ...form, [fieldName]: value }
        : form
    ));
  };

  // Handle DefaultEditor content changes
  const handleEditorChange = (formId, e) => {
    const content = e.target.value;
    setContentForms(prev => prev.map(form => 
      form.id === formId 
        ? { ...form, content: content }
        : form
    ));
  };

  // Handle DefaultEditor content_text changes
  const handleContentTextEditorChange = (formId, e) => {
    const contentText = e.target.value;
    setContentForms(prev => prev.map(form => 
      form.id === formId 
        ? { ...form, content_text: contentText }
        : form
    ));
  };


  const addContentForm = () => {
    // Find the highest order_index and add 1 for the new form
    const maxOrderIndex = Math.max(...contentForms.map(form => form.order_index || 0));
    const newOrderIndex = maxOrderIndex + 1;
    
    const newForm = {
      id: nextId,
      lesson_id: lessonId || 0,
      title: '',
      content_type: 0,
      content: '',
      content_text: '',
      quiz_id: null,
      question_id: null,
      order_index: newOrderIndex
    };
    setContentForms(prev => [...prev, newForm]);
    setNextId(prev => prev + 1);
  };

  const removeContentForm = (formId) => {
    if (contentForms.length > 1) {
      setContentForms(prev => prev.filter(form => form.id !== formId));
      // Clear any upload errors for this form
      setUploadError(prev => {
        const newErrors = { ...prev };
        delete newErrors[formId];
        return newErrors;
      });
    }
  };

  const handleAssetUploadSuccess = (formId, url, key) => {
    console.log('Asset uploaded successfully:', url);
    setContentForms(prev => prev.map(form => 
      form.id === formId 
        ? { ...form, content: url }
        : form
    ));
    setUploadError(prev => ({
      ...prev,
      [formId]: null
    }));
  };

  const handleAssetUploadError = (formId, error) => {
    console.error('Asset upload failed:', error);
    setUploadError(prev => ({
      ...prev,
      [formId]: error
    }));
  };

  const getAcceptedFileTypes = (contentType) => {
    switch (parseInt(contentType)) {
      case 2: // Video
        return 'video/*';
      case 4: // Image
        return 'image/*';
      case 'audio':
        return 'audio/*';
      case 'document':
        return '.pdf,.doc,.docx,.txt,.rtf';
      default:
        return '*/*';
    }
  };

  const getFolderName = (contentType) => {
    switch (parseInt(contentType)) {
      case 2: // Video
        return 'lesson-videos';
      case 4: // Image
        return 'lesson-images';
      case 'audio':
        return 'lesson-audio';
      case 'document':
        return 'lesson-documents';
      default:
        return 'lesson-content';
    }
  };

  const handleQuizSelection = (formId, e) => {
    const selectedQuizId = e.target.value;
    setContentForms(prev => prev.map(form => 
      form.id === formId 
        ? {
            ...form,
            quiz_id: selectedQuizId ? parseInt(selectedQuizId) : null,
            content: selectedQuizId ? selectedQuizId.toString() : ''
          }
        : form
    ));
  };

  const handleQuestionSelection = (formId, e) => {
    const selectedQuestionId = e.target.value;
    setContentForms(prev => prev.map(form => 
      form.id === formId 
        ? {
            ...form,
            question_id: selectedQuestionId ? parseInt(selectedQuestionId) : null,
            content: selectedQuestionId ? selectedQuestionId.toString() : ''
          }
        : form
    ));
  };
  
  const renderContentInput = (form, formIndex) => {
    const contentType = parseInt(form.content_type);
    
    switch (contentType) {
      case 1: // Text Content
        return (
          <div>
            <Label htmlFor={`content_${form.id}`}>Text Content *</Label>
            <div className="mt-1">
              <DefaultEditor
                value={form.content}
                onChange={(e) => handleEditorChange(form.id, e)}
                containerProps={{
                  style: {
                    minHeight: '300px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px'
                  }
                }}
                editorProps={{
                  disabled: isSubmitting,
                  placeholder: 'Enter text content, HTML, or other text-based content...'
                }}
              />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Enter the text content for this lesson section. You can use rich text formatting.
            </p>
          </div>
        );
        
      case 2: // Video/File Upload
        return (
          <div>
            <Label htmlFor={`content_file_${form.id}`}>Video File *</Label>
            <div className="mt-1">
              <FileUpload
                onUploadSuccess={(url, key) => handleAssetUploadSuccess(form.id, url, key)}
                onUploadError={(error) => handleAssetUploadError(form.id, error)}
                onFileSelect={(url, key) => {
                  // Direct file selection from existing S3 files
                  handleContentChange(form.id, 'content', url);
                  setUploadError(prev => ({ ...prev, [form.id]: undefined }));
                }}
                accept={getAcceptedFileTypes(form.content_type)}
                maxSize={5000 * 1024 * 1024} //  5000MB
                folder={getFolderName(form.content_type)}
                disabled={isSubmitting}
                showPreview={false}
                showFileBrowser={true}
                currentUrl={form.content.startsWith('http') ? form.content : '' }
                className="w-full"
              />
              {uploadError[form.id] && (
                <p className="mt-1 text-sm text-red-600">
                  Upload failed: {uploadError[form.id]}
                </p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Upload a new video file or select from existing uploads (max 5GB).
              </p>
              {form.content && (  
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                  <p className="text-sm text-green-700">
                    File selected: <a href={form.content} target="_blank" rel="noopener noreferrer" className="underline">View file</a>
                  </p>
                </div>
              )}
            </div>
          </div>
        );
        
      case 3: // Quiz
        return (
          <div>
            <Label htmlFor={`quiz_select_${form.id}`}>Select Quiz *</Label>
            <select
              id={`quiz_select_${form.id}`}
              name="quiz_select"
              value={form.quiz_id || ''}
              onChange={(e) => handleQuizSelection(form.id, e)}
              required
              disabled={isSubmitting || quizzesLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
            >
              <option value="">
                {quizzesLoading ? 'Loading quizzes...' : 'Select a quiz'}
              </option>
              {!quizzesLoading && quizzes.map((quiz) => (
                <option key={quiz.id} value={quiz.id}>
                  {quiz.title || `Quiz ${quiz.id}`}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              Select a quiz to embed in this lesson content.
            </p>
            {form.quiz_id && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-700">
                  Selected Quiz ID: {form.quiz_id}
                </p>
              </div>
            )}
          </div>
        );
        
      case 4: // Image Upload
        return (
          <div>
            <Label htmlFor={`content_image_${form.id}`}>Image File *</Label>
            <div className="mt-1">
              <FileUpload
                onUploadSuccess={(url, key) => handleAssetUploadSuccess(form.id, url, key)}
                onUploadError={(error) => handleAssetUploadError(form.id, error)}
                onFileSelect={(url, key) => {
                  // Direct file selection from existing S3 files
                  handleContentChange(form.id, 'content', url);
                  setUploadError(prev => ({ ...prev, [form.id]: undefined }));
                }}
                accept={getAcceptedFileTypes(form.content_type)}
                maxSize={50 * 1024 * 1024} // 50MB for images
                folder={getFolderName(form.content_type)}
                disabled={isSubmitting}
                showPreview={true}
                showFileBrowser={true}
                currentUrl={form.content.startsWith('http') ? form.content : ''}
                className="w-full"
              />
              {uploadError[form.id] && (
                <p className="mt-1 text-sm text-red-600">
                  Upload failed: {uploadError[form.id]}
                </p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Upload a new image file or select from existing uploads (max 50MB). Supported formats: JPG, PNG, GIF, WebP.
              </p>
              {form.content && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                  <p className="text-sm text-green-700">
                    Image selected: <a href={form.content} target="_blank" rel="noopener noreferrer" className="underline">View image</a>
                  </p>
                  {form.content.startsWith('http') && (
                    <div className="mt-2">
                      <img 
                        src={form.content} 
                        alt="Selected lesson content" 
                        className="max-w-full h-auto max-h-48 rounded border"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );

      case 5: // Question
        return (
          <div>
            <Label htmlFor={`question_select_${form.id}`}>Select Question *</Label>
            <select
              id={`question_select_${form.id}`}
              name="question_select"
              value={form.question_id || ''}
              onChange={(e) => handleQuestionSelection(form.id, e)}
              required
              disabled={isSubmitting || questionsLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
            >
              <option value="">
                {questionsLoading ? 'Loading questions...' : 'Select a question'}
              </option>
              {!questionsLoading && questions.map((question) => (
                <option key={question.id} value={question.id}>
                  {question.text || `Question ${question.id}`}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              Select a question to embed in this lesson content.
            </p>
            {form.question_id && (
              <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded">
                <p className="text-sm text-orange-700">
                  Selected Question ID: {form.question_id}
                </p>
              </div>
            )}
          </div>
        );
        
      default:
        return (
          <div>
            <Label htmlFor={`content_${form.id}`}>Content *</Label>
            <textarea
              id={`content_${form.id}`}
              name="content"
              value={form.content}
              onChange={(e) => handleInputChange(form.id, e)}
              rows="4"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed"
              placeholder="Enter content..."
              disabled={isSubmitting}
            />
            <p className="mt-1 text-sm text-gray-500">
              Please select a content type first.
            </p>
          </div>
        );
    }
  };

  const handleDeleteLessonContent = async (contentId, formId) => {

    if (!window.confirm('Are you sure you want to delete this lesson content? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingContentId(contentId);
      console.log('Deleting lesson content with ID:', contentId);
      await lessonContentAPI.deleteLessonContent(contentId);
      
      // Remove the content from local state
      setContentForms(prev => prev.filter(form => form.id !== formId));
      
      // Clear lesson content cache
      cacheUtils.clearLessonContentCache();
      
      // Call callback to notify parent component
      if (onContentDeleted) {
        onContentDeleted(contentId);
      }
      
      console.log('Lesson content deleted successfully');
    } catch (error) {
      console.error('Error deleting lesson content:', error);
      alert('Failed to delete lesson content. Please try again.');
    } finally {
      setDeletingContentId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      
      // Validate all forms
      const invalidForms = contentForms.filter(form => 
        !form.content_type || parseInt(form.content_type) === 0 || !form.content.trim()
      );
      
      if (invalidForms.length > 0) {
        alert('Please fill in all required fields for all content forms.');
        return;
      }

      if (editingContent || (isViewingMode && existingContents.length > 0)) {
        // Update existing content(s)
        const updatedContents = [];
        
        for (let i = 0; i < contentForms.length; i++) {
          const form = contentForms[i];
          
          // Skip if no existing_id (newly added forms)
          if (isViewingMode && !form.existing_id) {
            continue;
          }
          
          // Prepare content field based on content type
          let contentValue = form.content;
          if (parseInt(form.content_type) === 1) {
            // Case 1: Text content - use text string
            contentValue = form.content;
          } else if (parseInt(form.content_type) === 2) {
            // Case 2: Video - use uploaded file URL
            contentValue = form.content;
          } else if (parseInt(form.content_type) === 3) {
            // Case 3: Quiz - use quiz ID as string
            contentValue = form.quiz_id ? form.quiz_id.toString() : '';
          } else if (parseInt(form.content_type) === 5) {
            // Case 5: Question - use question ID as string
            contentValue = form.question_id ? form.question_id.toString() : '';
          }
          
          const submitData = {
            lesson_id: parseInt(form.lesson_id),
            title: form.title || '',
            content_type: parseInt(form.content_type),
            content: contentValue,
            content_text: form.content_text || '',
            order_index: parseInt(form.order_index) || 1,
            quiz_id: form.quiz_id ? parseInt(form.quiz_id) : null,
            question_id: form.question_id ? parseInt(form.question_id) : null
          };
          
          console.log(`Updating lesson content ${i + 1}:`, submitData);
          const contentId = editingContent ? editingContent.id : form.existing_id;
          await lessonContentAPI.updateLessonContent(contentId, submitData);
          updatedContents.push(contentId);
        }
        
        // Handle new content creation in viewing mode
        const newContents = [];
        if (isViewingMode) {
          const newContentForms = contentForms.filter(form => !form.existing_id);

          for (let i = 0; i < newContentForms.length; i++) {
            const form = newContentForms[i];
            
            // Prepare content field based on content type
            let contentValue = form.content;
            if (parseInt(form.content_type) === 1) {
              contentValue = form.content;
            } else if (parseInt(form.content_type) === 2) {
              contentValue = form.content;
            } else if (parseInt(form.content_type) === 3) {
              contentValue = form.quiz_id ? form.quiz_id.toString() : '';
            } else if (parseInt(form.content_type) === 5) {
              contentValue = form.question_id ? form.question_id.toString() : '';
            }
            
            const submitData = {
              lesson_id: parseInt(form.lesson_id),
              title: form.title || '',
              content_type: parseInt(form.content_type),
              content: contentValue,
              content_text: form.content_text || '',
              order_index: parseInt(form.order_index) || 1,
              quiz_id: form.quiz_id ? parseInt(form.quiz_id) : null,
              question_id: form.question_id ? parseInt(form.question_id) : null
            };
            
            console.log(`Creating new lesson content ${i + 1}:`, submitData);
            const response = await lessonContentAPI.createLessonContent(submitData);
            newContents.push(response);
          }
        }
        
        const totalOperations = updatedContents.length + newContents.length;
        alert(`Successfully ${updatedContents.length > 0 ? `updated ${updatedContents.length}` : ''}${updatedContents.length > 0 && newContents.length > 0 ? ' and ' : ''}${newContents.length > 0 ? `created ${newContents.length}` : ''} lesson content(s)!`);
      } else if (editingContent) {
        // Legacy single content update (keeping for backward compatibility)
        const form = contentForms[0];
        
        // Prepare content field based on content type
        let contentValue = form.content;
        if (parseInt(form.content_type) === 1) {
          // Case 1: Text content - use text string
          contentValue = form.content;
        } else if (parseInt(form.content_type) === 2) {
          // Case 2: Video - use uploaded file URL
          contentValue = form.content;
        } else if (parseInt(form.content_type) === 3) {
          // Case 3: Quiz - use quiz ID as string
          contentValue = form.quiz_id ? form.quiz_id.toString() : '';
        } else if (parseInt(form.content_type) === 5) {
          // Case 5: Question - use question ID as string
          contentValue = form.question_id ? form.question_id.toString() : '';
        }
        
        const submitData = {
          lesson_id: parseInt(form.lesson_id),
          title: form.title || '',
          content_type: parseInt(form.content_type),
          content: contentValue,
          content_text: form.content_text || '',
          order_index: parseInt(form.order_index) || 1,
          quiz_id: form.quiz_id ? parseInt(form.quiz_id) : null,
          question_id: form.question_id ? parseInt(form.question_id) : null
        };
        
        console.log('Updating lesson content:', submitData);
        await lessonContentAPI.updateLessonContent(editingContent.id, submitData);
        alert('Lesson content updated successfully!');
      } else {
        // Create multiple new contents
        const createdContents = [];
        
        for (let i = 0; i < contentForms.length; i++) {
          const form = contentForms[i];
          
          // Prepare content field based on content type
          let contentValue = form.content;
          if (parseInt(form.content_type) === 1) {
            // Case 1: Text content - use text string
            contentValue = form.content;
          } else if (parseInt(form.content_type) === 2) {
            // Case 2: Video - use uploaded file URL
            contentValue = form.content;
          } else if (parseInt(form.content_type) === 3) {
            // Case 3: Quiz - use quiz ID as string
            contentValue = form.quiz_id ? form.quiz_id.toString() : '';
          } else if (parseInt(form.content_type) === 5) {
            // Case 5: Question - use question ID as string
            contentValue = form.question_id ? form.question_id.toString() : '';
          }
          
          const submitData = {
            lesson_id: parseInt(form.lesson_id),
            title: form.title || '',
            content_type: parseInt(form.content_type),
            content: contentValue,
            order_index: parseInt(form.order_index) || 1,
            quiz_id: form.quiz_id ? parseInt(form.quiz_id) : null,
            question_id: form.question_id ? parseInt(form.question_id) : null
          };
          console.log(`Creating lesson content ${i + 1}:`, submitData);
          const response = await lessonContentAPI.createLessonContent(submitData);
          createdContents.push(response);
        }
        
        alert(`Successfully created ${createdContents.length} lesson content(s)!`);
        console.log('Created lesson contents:', createdContents);
      }
      
      // Clear lesson content cache
      cacheUtils.clearLessonContentCache();
      
      // Call success callback
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
      
    } catch (error) {
      console.error('Error saving lesson content:', error);
      alert(`Failed to ${editingContent ? 'update' : 'create'} lesson content. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setContentForms([{
      id: 1,
      lesson_id: lessonId || '',
      title: '',
      content_type: 0,
      content: '',
      content_text: '',
      quiz_id: null,
      question_id: null,
      order_index: 1
    }]);
    setNextId(2);
    setUploadError({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} disableBackdropClose={true}>
      <form onSubmit={handleSubmit}>
        <ModalHeader>
          <h2 className="text-xl font-semibold text-gray-900">
            {isViewingMode 
              ? 'View Lesson Contents' 
              : editingContent 
                ? 'Edit Lesson Content' 
                : 'Create Lesson Content'
            }
          </h2>
          {lessonTitle && (
            <p className="text-sm text-gray-600 mt-1">
              For lesson: {lessonTitle}
            </p>
          )}
          {isViewingMode && existingContents.length === 0 && (
            <p className="text-sm text-yellow-600 mt-1">
              No content found for this lesson.
            </p>
          )}
        </ModalHeader>

        <ModalBody className="space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Content Forms */}
          {contentForms.map((form, index) => (
            <div key={form.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Content #{index + 1}
                </h3>
                <div className="flex items-center space-x-2">
                  {isViewingMode && form.existing_id && (
                    <button
                      type="button"
                      onClick={() => handleDeleteLessonContent(form.existing_id, form.id)}
                      disabled={deletingContentId === form.existing_id}
                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete this content"
                    >
                      {deletingContentId === form.existing_id ? (
                        <div className="animate-spin w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  )}
                  {((contentForms.length > 1 && !isViewingMode) || (isViewingMode && !form.existing_id)) && (
                    <button
                      type="button"
                      onClick={() => removeContentForm(form.id)}
                      disabled={isSubmitting}
                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50"
                      title="Remove this content"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                {/* Title */}
                <div>
                  <Label htmlFor={`title_${form.id}`}>Content Title</Label>
                  <input
                    id={`title_${form.id}`}
                    name="title"
                    type="text"
                    value={form.title}
                    onChange={(e) => handleInputChange(form.id, e)}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                    placeholder="Enter content title (optional)"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Optional title for this lesson content section.
                  </p>
                </div>

                {/* Content Type */}
                <div>
                  <Label htmlFor={`content_type_${form.id}`}>Content Type *</Label>
                  <select
                    id={`content_type_${form.id}`}
                    name="content_type"
                    value={form.content_type}
                    onChange={(e) => handleInputChange(form.id, e)}
                    required
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                  >
                    <option value={0}>Select content type</option>
                    {contentTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Dynamic Content Input */}
                {renderContentInput(form, index)}

                {/* Content Text Input */}
                <div>
                  <Label htmlFor={`content_text_${form.id}`}>Content Text</Label>
                  <div className="mt-1">
                    <DefaultEditor
                      value={form.content_text}
                      onChange={(e) => handleContentTextEditorChange(form.id, e)}
                      containerProps={{
                        style: {
                          minHeight: '200px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px'
                        }
                      }}
                      editorProps={{
                        disabled: isSubmitting,
                        placeholder: 'Enter additional content text, description, or notes...'
                      }}
                    />
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Additional text content or description for this lesson section.
                  </p>
                </div>

                {/* Order Input */}

                <div>
                  <Label htmlFor={`order_index_${form.id}`}>Display Order *</Label>
                  <input
                    id={`order_index_${form.id}`}
                    name="order_index"
                    type="number"
                    min="1"
                    defaultValue="1"
                    value={form.order_index !== undefined && form.order_index !== 0 ? form.order_index : (index + 1)}
                    onChange={(e) => handleInputChange(form.id, e)}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                    placeholder="Enter display order (1, 2, 3, ...)"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Set the display order for this content section. Lower numbers appear first.
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Add Content Button */}
          {!editingContent && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={addContentForm}
                disabled={isSubmitting}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>Add Another Content</span>
              </button>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {isViewingMode ? 'Close' : 'Cancel'}
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting 
              ? (isViewingMode 
                  ? 'Updating...' 
                  : (editingContent ? 'Updating...' : `Creating ${contentForms.length} Content(s)...`))
              : (isViewingMode 
                  ? 'Save Changes' 
                  : (editingContent ? 'Update Content' : `Create ${contentForms.length} Content(s)`))
            }
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default LessonContentModal;
